import React, { useMemo } from 'react';
import classes from './PostCardStatusTable.module.scss';
import { SocialProfile } from '../../socialProfiles/SocialProfile';
import { SocialProfileComponentBasicType } from '../../socialProfiles/SocialProfile/SocialProfile.view';
import { PostItemFragment, PostStatus, PostTiming } from '../../../models/graphql';
import { CardOrientationEnum } from '../BasePostCard/BasePostCard.view';
import { profilesHelpers } from '../../../modules/Settings/Profiles/Profiles.helpers';
import { DateStyle, useDateString } from '../../../hooks/useDateString';
import { useBreakpoints } from '../../../hooks/useBreakpoints';
import classNames from 'classnames';
import { IconsEnum } from '../../../constants/icons';
import { AttributeIcon } from '../../containers/AttributeIcon';
import { ScheduleHelpers } from '../../../modules/PostEditor/Schedule/Schedule.helpers';
import { useSelectedOrganization } from '../../../hooks/useSelectedOrganization';
import pluralize from 'pluralize';
import { PostScheduleViewContext } from '../../../modules/Publish/PostSchedule/PostSchedule.controller';
import { Tooltip } from 'primereact/tooltip';
import { idGenerator } from '../../../helpers/idGenerator';
import { TDateTimeString } from '../../../models/date';
import scssVars from '../../../assets/scss/exportedVars.module.scss';

export type PostCardStatusTableViewProps = {
  //
  post: PostItemFragment;
  orientation: CardOrientationEnum;
  context: PostScheduleViewContext;
  showMinimalIcons?: boolean;
  isDetailedPastPost?: boolean;
};

export const PostCardStatusTableView = (props: PostCardStatusTableViewProps): JSX.Element => {
  const { post, orientation, showMinimalIcons } = props;
  const { calculatedUtcDatetime, variationMeta, status, datePosted, dateFailed, dateAdded, timing } = post;

  const showStatusIcons = props.context !== PostScheduleViewContext.LIBRARY && !showMinimalIcons;

  const { formatDateString } = useDateString();
  const { selectedWorkspace } = useSelectedOrganization();
  const { timezone } = selectedWorkspace || {};
  const breakpoint = useBreakpoints();

  const id = useMemo(() => idGenerator(), []);

  const socialProfile: SocialProfileComponentBasicType = {
    id: post.socialProfile.id,
    provider: profilesHelpers.mapOAuthFromTypename(post.socialProfile.__typename),
    network: profilesHelpers.mapNetworkFromTypename(post.socialProfile.__typename),
    genericProfile:
      post.socialProfile.__typename === 'GenericSocialProfile' ? post.socialProfile.genericProfile : undefined,
    name: post.socialProfile.displayName,
    image: post.socialProfile.photoUrl
  };

  const scheduledFutureDates = useMemo(
    () =>
      post.futureScheduledLocalDateTimes.flatMap(date => {
        const isPast = timezone && ScheduleHelpers.checkIfScheduledPostIsPast(date, timezone);
        return isPast ? [] : date;
      }),
    [timezone, post]
  );

  const schedulingOnce =
    ((!post.prevPostedCount && status !== PostStatus.PUBLISHED && scheduledFutureDates.length === 1) ||
      (post.prevPostedCount === 1 && !scheduledFutureDates.length && status !== PostStatus.PUBLISHED) ||
      (!post.prevPostedCount && !scheduledFutureDates.length && status === PostStatus.PUBLISHED)) &&
    timing === PostTiming.SCHEDULE &&
    orientation !== CardOrientationEnum.CALENDAR &&
    showStatusIcons;

  const hasSchedulingIcon =
    timing === PostTiming.SCHEDULE &&
    orientation !== CardOrientationEnum.CALENDAR &&
    showStatusIcons &&
    status !== PostStatus.FAILED &&
    !schedulingOnce;

  const lastScheduledDate = scheduledFutureDates.length ? scheduledFutureDates[scheduledFutureDates.length - 1] : '';

  let displayDate: TDateTimeString;
  switch (status) {
    case PostStatus.ACTIVE:
      displayDate = calculatedUtcDatetime || '';
      break;
    case PostStatus.PUBLISHED:
      displayDate = datePosted || '';
      break;
    case PostStatus.FAILED:
      displayDate = dateFailed || '';
      break;
    default:
      displayDate = dateAdded || '';
      break;
  }

  const formattedDisplayDate = useMemo(() => {
    const dateStyle =
      orientation === CardOrientationEnum.LANDSCAPE
        ? ['medium', 'small'].includes(breakpoint)
          ? DateStyle.TIME_ONLY
          : DateStyle.WITH_DAY_OF_WEEK
        : DateStyle.TIME_ONLY;

    return formatDateString(displayDate, dateStyle, true);
  }, [breakpoint, displayDate, formatDateString, orientation]);

  const tooltipDateText = useMemo(() => {
    const dateText = formatDateString(displayDate, DateStyle.WITH_DAY_OF_WEEK, true);
    switch (status) {
      case PostStatus.ACTIVE:
        return `This post will be published on ${dateText}`;
      case PostStatus.PUBLISHED:
        return `This post was published on ${dateText}`;
      case PostStatus.FAILED:
        return `This post was scheduled to be published on ${dateText}`;
      default:
        return '';
    }
  }, [status, displayDate, formatDateString]);

  const outputDate = useMemo(() => {
    return (
      <>
        <div className={classNames('cell', 'shrink', classes.DateText)} id={`date-tooltip-${id}`}>
          {formattedDisplayDate}
        </div>
        <Tooltip
          showDelay={20}
          hideDelay={0}
          className={'p-tooltip-dark-pointer'}
          target={`#date-tooltip-${id}`}
          position={'bottom'}
          autoZIndex={false}
          style={{ zIndex: scssVars['z-index-overmodal'] }}>
          {tooltipDateText}
        </Tooltip>
      </>
    );
  }, [id, tooltipDateText, formattedDisplayDate]);

  const socialProfileElem = (
    <div className={classNames('cell', 'shrink', classes.SocialProfileWrapper, 'relative')}>
      <SocialProfile
        socialProfile={socialProfile}
        size={'small'}
        image={socialProfile.image}
        disconnected={!post.socialProfile.connected}
      />
    </div>
  );

  const { currentIndex, hasMultiple, total } = variationMeta || {};

  const variationIcon = () => {
    if (currentIndex === undefined || !hasMultiple || total === undefined || !showStatusIcons) return null;
    switch (orientation) {
      case CardOrientationEnum.CALENDAR:
        return (
          <AttributeIcon
            useInvew={false}
            icon={IconsEnum.variation}
            toolTip={`Post Variation ${currentIndex}/${total}`}
          />
        );
      default:
        return (
          <AttributeIcon
            icon={IconsEnum.variation}
            toolTip={`Post Variation ${currentIndex}/${total}`}>{`${currentIndex}/${total}`}</AttributeIcon>
        );
    }
  };

  const schedulingIcon = () => {
    if (!hasSchedulingIcon || !showStatusIcons) return null;
    const postedCount = status === PostStatus.PUBLISHED ? (post.prevPostedCount ?? 0) + 1 : post.prevPostedCount ?? 0;
    // if (post.prevPostedCount && post.scheduledLocalDateTimes.length <= post.prevPostedCount) return null;
    const total = postedCount + scheduledFutureDates.length;
    const currentCount = status === PostStatus.PUBLISHED ? postedCount : postedCount + 1;
    // const currentCount = typeof post.prevPostedCount === 'number' ? post.prevPostedCount + 1 : 1;
    const tooltipText =
      status === PostStatus.PUBLISHED
        ? `This post is scheduled for multiple dates, so far it has been published ${pluralize(
            'time',
            currentCount,
            true
          )}.`
        : `This post is scheduled for multiple dates, currently on cycle ${currentCount}/${total}`;
    return (
      <AttributeIcon icon={IconsEnum.multidate} toolTip={tooltipText}>
        {status === PostStatus.PUBLISHED ? '' : `${currentCount}/${total}`}
      </AttributeIcon>
    );
  };

  const getTooltipText = (
    expiryDate: PostItemFragment['expiryLocalDate'],
    expiryCycles: PostItemFragment['maxRepeats'],
    prevPostedCount: PostItemFragment['prevPostedCount'],
    type: 'schedule' | 'queue' | 'recurring'
  ): { label: string; tooltip: string } => {
    const postedCount = status === PostStatus.PUBLISHED ? (prevPostedCount ?? 0) + 1 : prevPostedCount || 0;
    const cycle = status === PostStatus.PUBLISHED ? postedCount : (prevPostedCount ?? 0) + 1;
    switch (type) {
      case 'schedule': {
        let tooltip = hasSchedulingIcon
          ? `After the last scheduled date, this post will be added to the bottom of the Queue`
          : schedulingOnce
          ? `After publishing on ${formatDateString(
              lastScheduledDate,
              DateStyle.WITH_DAY_OF_WEEK
            )}, this post will be added to the bottom of the Queue`
          : 'This post will be recycled';
        if (expiryDate && expiryCycles) {
          tooltip = `${tooltip} for ${expiryCycles} cycles, or until it expires on ${formatDateString(
            expiryDate,
            DateStyle.DATE_ONLY
          )}.`;
        } else if (expiryDate) {
          tooltip = `${tooltip} until it expires on ${formatDateString(expiryDate, DateStyle.DATE_ONLY)}.`;
        } else if (expiryCycles) {
          tooltip = `${tooltip} for ${expiryCycles} cycles.`;
        }
        return { label: '', tooltip };
      }
      case 'queue':
      case 'recurring': {
        const postTypeText = type === 'queue' ? 'Evergreen' : 'Recurring';
        if ((!expiryDate && !expiryCycles) || post.status === PostStatus.PUBLISHED) {
          return {
            tooltip: postedCount
              ? `This ${postTypeText} post has been published ${pluralize('time', postedCount, true)}`
              : `This ${postTypeText} post has not been published yet.`,
            label: orientation === CardOrientationEnum.LANDSCAPE ? `${cycle}` : ''
          };
        } else if (expiryDate && expiryCycles) {
          let label = '';
          if (
            orientation === CardOrientationEnum.LANDSCAPE &&
            ['tabletportrait', 'large', 'xlarge', 'xxlarge'].includes(breakpoint)
          ) {
            label = `${cycle}/${expiryCycles} (exp. ${formatDateString(expiryDate, DateStyle.DATE_ONLY)})`;
          } else if (orientation === CardOrientationEnum.LANDSCAPE) {
            label = `${cycle}/${expiryCycles} (exp)`;
          }
          return {
            tooltip: postedCount
              ? `This ${postTypeText} post has been published ${pluralize(
                  'time',
                  postedCount,
                  true
                )} and it will expire after ${pluralize('cycle', expiryCycles, true)} or on ${formatDateString(
                  expiryDate,
                  DateStyle.DATE_ONLY
                )}.`
              : `This ${postTypeText} post has not been published yet. It will expire after ${pluralize(
                  'cycle',
                  expiryCycles,
                  true
                )} or on ${formatDateString(expiryDate, DateStyle.DATE_ONLY)}.`,
            label
          };
        } else if (expiryCycles && !expiryDate) {
          const label = orientation === CardOrientationEnum.LANDSCAPE ? `${cycle}/${expiryCycles}` : '';
          const tooltip = postedCount
            ? `This ${postTypeText} post has been published ${pluralize(
                'time',
                postedCount,
                true
              )} and it will expire after ${pluralize('cycle', expiryCycles, true)}.`
            : `This ${postTypeText} post has not been published yet. It will expire after ${pluralize(
                'cycle',
                expiryCycles,
                true
              )}`;
          return { label, tooltip };
        } else if (expiryDate && !expiryCycles) {
          let label = '';
          if (
            orientation === CardOrientationEnum.LANDSCAPE &&
            ['tabletportrait', 'large', 'xlarge', 'xxlarge'].includes(breakpoint)
          ) {
            label = `${cycle} (exp. ${formatDateString(expiryDate, DateStyle.DATE_ONLY)})`;
          } else if (orientation === CardOrientationEnum.LANDSCAPE) {
            label = `${cycle} (exp)`;
          }
          const tooltip = postedCount
            ? `This ${postTypeText} post has been published ${pluralize(
                'time',
                postedCount,
                true
              )} and it will expire after on ${formatDateString(expiryDate, DateStyle.DATE_ONLY)}.`
            : `This ${postTypeText} post has not been published yet. It will expire on ${formatDateString(
                expiryDate,
                DateStyle.DATE_ONLY
              )}.`;
          return { label, tooltip };
        }
        return { label: '', tooltip: '' };
        // this line will never be reached since we covered all the possible combinations, but ts complains with TS7029: Fallthrough case in switch.
      }
    }
  };

  const evergreenIcon = () => {
    if (
      !post.isEvergreen ||
      orientation === CardOrientationEnum.CALENDAR ||
      !showStatusIcons ||
      ![PostTiming.SCHEDULE, PostTiming.QUEUE].includes(timing)
    )
      return null;
    const { expiryLocalDate: expiryDate, maxRepeats: expiryCycles, prevPostedCount } = post;
    const { tooltip, label } = getTooltipText(
      expiryDate,
      expiryCycles,
      prevPostedCount,
      timing === PostTiming.SCHEDULE ? 'schedule' : 'queue'
    );
    return (
      <AttributeIcon icon={IconsEnum.cycles} iconColor={'GREEN'} toolTip={tooltip}>
        {label}
      </AttributeIcon>
    );
  };

  const recurringIcon = () => {
    if (orientation === CardOrientationEnum.CALENDAR || timing !== PostTiming.RECURRING || !showStatusIcons)
      return null;
    const { expiryLocalDate: expiryDate, maxRepeats: expiryCycles, prevPostedCount: postedCount } = post;
    const { tooltip, label } = getTooltipText(expiryDate, expiryCycles, postedCount, 'recurring');
    return (
      <AttributeIcon icon={IconsEnum.cycles} toolTip={tooltip}>
        {label}
      </AttributeIcon>
    );
  };

  const delayedIcon = () => {
    const delayedDate = post.delayedUtcOriginalDatetime
      ? formatDateString(post.delayedUtcOriginalDatetime, DateStyle.WITH_DAY_OF_WEEK, true)
      : '';
    return delayedDate && showStatusIcons ? (
      <AttributeIcon
        icon={IconsEnum.delay}
        toolTip={`This post was originally due to be published on ${delayedDate}, but it was postponed to prevent the same content
from being posted too soon after the last cycle.`}
      />
    ) : null;
  };

  switch (orientation) {
    case CardOrientationEnum.CALENDAR:
      return (
        <div
          className={classNames('grid-x', 'align-middle', 'align-justify', 'margin-bottom-small-8', classes.Width100)}>
          <div className={'cell shrink'}>
            <div className={classNames('grid-x', 'align-middle')}>
              {socialProfileElem}
              {outputDate}
            </div>
          </div>
          {variationIcon()}
        </div>
      );
    case CardOrientationEnum.LANDSCAPE:
    case CardOrientationEnum.PORTRAIT:
      return (
        <div className={classNames('grid-x', 'align-middle')}>
          {socialProfileElem}
          {showStatusIcons ? outputDate : undefined}
          {!props.isDetailedPastPost && (
            <>
              {schedulingIcon()}
              {evergreenIcon()}
              {recurringIcon()}
              {delayedIcon()}
              {variationIcon()}
            </>
          )}
        </div>
      );
  }
};
